import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SvgIconComponent } from 'angular-svg-icon';
import { SkipLinksService } from '../../services/skip-links.service';

/**
 * Skip links component for keyboard navigation accessibility.
 *
 * @remarks Provides skip navigation links that allow users to bypass repetitive
 * navigation elements and jump directly to main content areas. Essential for
 * keyboard users and screen readers to improve navigation efficiency and
 * accessibility compliance.
 */
@Component({
  selector: 'app-skip-links',
  templateUrl: './skip-links.component.html',
  styleUrls: ['./skip-links.component.scss'],
  standalone: true,
  imports: [CommonModule, SvgIconComponent],
})
export class SkipLinksComponent {
  /**
   * Array of skip link configurations with target IDs and labels.
   *
   * @remarks Defines the available skip navigation options including
   * main content, navigation, and footer landmarks. Each link provides
   * a German label and corresponding element ID for navigation.
   */
  skipLinks = [
    { id: 'main-content', label: 'Zum Hauptinhalt springen' },
    { id: 'main-navigation', label: 'Zur Hauptnavigation springen' },
    { id: 'main-footer', label: 'Zum Footer springen' },
  ];

  /**
   * Creates an instance of SkipLinksComponent.
   *
   * @param skipLinksService - Service for managing skip link focus state
   */
  constructor(private skipLinksService: SkipLinksService) {}

  /**
   * Handles focus events on skip links.
   *
   * @remarks Notifies the skip links service that a skip link has received
   * focus, allowing other components to react to this state change for
   * coordinated accessibility behavior.
   */
  onFocus(): void {
    this.skipLinksService.setFocused(true);
  }

  /**
   * Handles blur events on skip links.
   *
   * @remarks Notifies the skip links service that focus has left the skip
   * links area, updating the global focus state for accessibility coordination.
   */
  onBlur(): void {
    this.skipLinksService.setFocused(false);
  }

  /**
   * Navigates to a specific landmark element on the page.
   *
   * @remarks Handles skip link activation by finding the target landmark
   * element and programmatically setting focus to it. Updates the service
   * state and prevents default link behavior for proper accessibility.
   *
   * @param event - The click or keyboard event that triggered navigation
   * @param landmarkId - The ID of the target landmark element to focus
   */
  skipToLandmark(event: Event, landmarkId: string): void {
    event.preventDefault();
    const landmark = document.getElementById(landmarkId);
    if (landmark) {
      this.skipLinksService.setFocused(false);
      landmark.focus();
    }
  }
}
